import { BOARD_MODES } from './board-modes.js'

export const CARD_KINDS = [
  { id: 'idea', label: '观点', color: '#f2e3b3' },
  { id: 'source', label: '资料', color: '#e0d2f0' },
  { id: 'character', label: '人物', color: '#f3d0ba' },
  { id: 'plot', label: '情节', color: '#d6e6d3' },
  { id: 'note', label: '灵感', color: '#d6e9ec' },
  { id: 'question', label: '疑问', color: '#f4ddd6' },
  { id: 'link', label: '关联', color: '#f6e2c4' },
  { id: 'summary', label: '总结', color: '#e8d5a8' },
]

export const LINK_KINDS = ['关联', '支持', '反驳', '延伸', '引用']

export function kindById(id) {
  return CARD_KINDS.find((item) => item.id === id) || CARD_KINDS[0]
}

export function emptyCanvas() {
  return { cards: [], links: [], books: [], levels: [], frameworks: [], frameworkId: null, view: { collapsed: {}, collapsedLevels: [], openMap: {} } }
}

export function cardTintOf(card) {
  return card.color || kindById(card.kind).color
}
const MASLOW_LAYERS = [
  { name: '自我实现', note: '创造、意义、成长', color: '#c9a24d' },
  { name: '尊重需求', note: '认可、地位、自尊', color: '#b0815f' },
  { name: '社交需求', note: '归属、友谊、爱', color: '#8fa58b' },
  { name: '安全需求', note: '稳定、健康、保护', color: '#8b9cb5' },
  { name: '生理需求', note: '食物、水、睡眠', color: '#6f7d72' },
  { name: '生存需求', note: '呼吸、温度、避难', color: '#4f5d52' },
]

export const FRAMEWORKS = [
  {
    id: 'fw-maslow',
    name: '需求金字塔',
    kind: 'pyramid',
    description: '马斯洛需求金字塔：从自我实现到生存需求的六层。',
    groups: MASLOW_LAYERS.map((layer) => ({ name: layer.name, note: layer.note, color: layer.color, items: [] })),
  },
  ...BOARD_MODES.map((mode) => ({
    id: 'fw-' + mode.id,
    name: mode.label,
    kind: 'columns',
    description: mode.description || mode.columns.map((column) => column.label).join(' / '),
    groups: mode.columns.map((column) => ({ name: column.label, note: '', color: column.color, items: [] })),
  })),
  {
    id: 'tragedy9',
    name: '悲剧分析九问',
    kind: 'questions',
    description: '三层九问：认知层看信念，行动层看反抗，物化层看规则与物件。',
    groups: [
      {
        name: '认知层',
        color: '#c9a24d',
        items: [
          '这个人物相信什么？把「信念台词」找出来（威利的「人缘就是全部」、克瑞翁的「城邦至上」）。',
          '这个信念是被证伪、被蒸发，还是从未存在？三者对应的悲剧形态完全不同。',
          '他有没有「看见」的时刻？看见了什么——真理、自己的价格，还是什么都没看见？',
        ],
      },
      {
        name: '行动层',
        color: '#b0815f',
        items: [
          '他有没有一个明确的动作指向墙？如果有，那个动作是攻击、绕过、还是跪下？',
          '他用来反抗的手段，是不是恰好是墙教给他的？（摧毁型的判准）',
          '如果没有动作，是什么让他留在墙上？惯性、恐惧、还是已经无法想象墙外？',
        ],
      },
      {
        name: '物化层',
        color: '#6f7d72',
        items: [
          '规则在这部作品里被叫什么名字？（「公司」「制度」「命运」「体面」「考核」）——命名本身就是物化行为。',
          '有没有一个物件承载了人物的全部价值？（祥子的车、威利的保险单、周朴园的旧家具）找到那个物件，物化分析就有了锚点。',
          '观众/读者能不能给这场苦难归因？能归因到某个人，是情节剧；归因到系统，是社会学；归不了因，才是悲剧。',
        ],
      },
    ],
  },
]

const KIND_MAP = { plot: 'plot', character: 'character', source: 'source', outline: 'idea' }
const KIND_COLORS = { plot: '#d6e6d3', character: '#f3d0ba', source: '#e0d2f0', outline: '#f2e3b3' }

export function migrateProjectsToCanvas(projects) {
  const cards = []
  const links = []
  const seen = new Set()
  let index = 0
  const place = (card) => {
    card.x = 80 + (index % 5) * 260
    card.y = 80 + Math.floor(index / 5) * 220
    index += 1
    return card
  }
  const cardPos = (id) => {
    const found = cards.find((item) => item.id === id)
    return found ? { x: found.x, y: found.y } : { x: 80, y: 80 }
  }
  for (const project of projects || []) {
    for (const card of project.cards || []) {
      const kind = KIND_MAP[card.kind] || 'idea'
      const canvasCard = place({
        id: card.id,
        kind,
        title: card.title || '',
        content: card.content || '',
        color: card.color || KIND_COLORS[card.kind] || '#f2e3b3',
        status: 'active',
        column: card.column || 'inspiration',
        act: card.act || '未分幕',
        createdAt: card.createdAt || new Date().toISOString(),
        updatedAt: card.updatedAt || new Date().toISOString(),
      })
      cards.push(canvasCard)
      seen.add(card.id)
    }
    for (const item of project.outline || []) {
      cards.push(place({
        id: 'out-' + item.id,
        kind: 'idea',
        title: item.title || '',
        content: item.note || '',
        color: '#f2e3b3',
        status: 'active',
        column: 'inspiration',
        act: item.act || '未分幕',
      }))
    }
    // 旧关联线同样补成「关联卡 + 两条连线」：每条线都有卡片解释原因
    for (const connection of project.connections || []) {
      if (!connection || !seen.has(connection.source) || !seen.has(connection.target)) continue
      const from = cardPos(connection.source)
      const to = cardPos(connection.target)
      const linkCardId = 'link-' + connection.id + '-card'
      const reason = connection.label || '关联'
      cards.push({
        id: linkCardId,
        kind: 'link',
        title: reason,
        content: connection.anchor && connection.anchor.text ? connection.anchor.text : '',
        color: '#f6e2c4',
        x: Math.round((from.x + to.x) / 2),
        y: Math.round((from.y + to.y) / 2),
        status: 'active',
        column: 'inspiration',
        act: '未分幕',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      })
      links.push({ id: 'link-' + connection.id + '-a', source: connection.source, target: linkCardId, label: '关联', directed: true })
      links.push({ id: 'link-' + connection.id + '-b', source: linkCardId, target: connection.target, label: '关联', directed: true })
    }
  }
  return { cards, links, books: [], levels: [], frameworks: [], frameworkId: null, view: { collapsed: {}, collapsedLevels: [], openMap: {} } }
}

export function mergeMigrated(base, migrated) {
  const existingIds = new Set((base.cards || []).map((card) => card.id))
  const existingLinks = new Set((base.links || []).map((link) => link.source + '->' + link.target))
  return {
    ...base,
    cards: [...(base.cards || []), ...migrated.cards.filter((card) => !existingIds.has(card.id))],
    links: [...(base.links || []), ...migrated.links.filter((link) => !existingLinks.has(link.source + '->' + link.target))],
    migratedFromProjects: true,
  }
}


